<script lang="ts">
import { mapActions, mapGetters } from "vuex"
import GlobalSidebarMenu from "@/components/GlobalSidebarMenu.vue"
import { translateForOemToLower } from "@/utils/i18n-utils"

export default {
    name: "GlobalSidebar",
    components: {
        GlobalSidebarMenu,
    },
    props: {
        companies: { type: Array, required: true },
    },
    data() {
        return {
            isOnMobile: false,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            isFooterOpen: true,
        }
    },
    methods: {
        ...mapActions("sidebar", {
            setSidebarVisibility: "setSidebarVisibility",
            toggleVisibility: "toggleVisibility",
            setCompany: "setCompany",
        }),
        handleResize() {
            this.windowWidth = window.innerWidth
            this.windowHeight = window.innerHeight
        },
        getGlobalMenu() {
            return this.companies.map((company: any) => {
                const translDevices = this.oemDeviceTranslate("Devices")

                return {
                    title: company.name,
                    isAggregator: true,
                    type: "company",
                    data: {
                        company: company,
                    },
                    level: 1,
                    aliases: [
                        `/settings/global/companies/${company.id}`,
                        `/settings/global/companies/${company.id}\\?(.*)`,
                        `/settings/global/companies/${company.id}/(.*)`,
                    ],
                    href: `/settings/global/companies/${company.id}`,
                    children: [
                        {
                            title: this.$t("Locations & Groups"),
                            isAggregator: false,
                            href: `/settings/global/companies/${company.id}/location-groups`,
                            type: null,
                            level: 2,
                            data: {
                                company: company,
                            },
                            children: [],
                            aliases: [
                                `/settings/global/companies/${company.id}/location-groups`,
                                `/settings/global/companies/${company.id}/location-groups\\?(.*)`,
                            ],
                        },
                        {
                            title: translDevices,
                            isAggregator: false,
                            href: `/settings/global/companies/${company.id}/oem-devices`,
                            type: null,
                            level: 2,
                            data: {
                                company: company,
                            },
                            children: [],
                            aliases: [`/settings/global/companies/${company.id}/oem-devices`],
                        },
                        {
                            title: this.$t("Users"),
                            isAggregator: false,
                            href: `/settings/global/companies/${company.id}/users`,
                            type: null,
                            level: 2,
                            data: {
                                company: company,
                            },
                            children: [],
                            aliases: [`/settings/global/companies/${company.id}/users`],
                        },
                    ],
                }
            })
        },
        closeSidebar() {
            if (this.isOnMobile) {
                this.setSidebarVisibility(false)
            }
        },
        onItemClick(event: any, item: any, node: any) {
            this.$store.commit("reduxi/setPageTitle", item.title)
        },
        onItemClick2() {
            if (this.isOnMobile) {
                this.setSidebarVisibility(false)
            }
        },
        logout() {
            this.$store.dispatch("auth/logout")
            //@ts-ignore
            this.$router.push("/auth/login").catch(() => {})
        },
        saveSidebarState() {
            if (!this.isOnMobile) {
                localStorage.setItem("sidebarVisible", JSON.stringify(this.isSidebarVisible))
            }
        },
        loadSidebarState() {
            if (!this.isOnMobile) {
                const savedState = localStorage.getItem("sidebarVisible")
                if (savedState !== null) {
                    return JSON.parse(savedState)
                }
            }
            return null
        },
        onResize() {
            this.isOnMobile = window.innerWidth <= 768

            if (!this.isOnMobile) {
                const savedState = this.loadSidebarState()
                if (savedState !== null) {
                    this.setSidebarVisibility(savedState)
                } else {
                    this.setSidebarVisibility(true)
                }
            } else {
                this.setSidebarVisibility(false)
            }

            if (this.isMobileLandscape) {
                this.isFooterOpen = false
            } else {
                this.isFooterOpen = true
            }
        },
        onExitImpersonationClick() {
            this.$store.dispatch("auth/unimpersonate")
        },
        sidebarMaxWidth: function () {
            if (this.isOnMobile) {
                return this.isSidebarVisible ? "100%" : "0"
            }

            const sidebarWidth = this.isSidebarVisible ? "360px" : "50px"

            // Dynamically set the contentRef padding-left
            if (this.contentRef) {
                this.contentRef.style.marginLeft = sidebarWidth
            }

            return sidebarWidth
        },
        onManageClick() {
            //@ts-ignore
            if (!this.showManageOption) {
                return
            }

            this.onItemClick2()
            //@ts-ignore
            this.$router.push(this.manageUrl)
        },
        toggleFooter() {
            this.isFooterOpen = !this.isFooterOpen
        },
        goToHome() {
            if (this.$permissions.hasSuperAdminRole()) {
                return { path: "/" }
            }

            if (this.$permissions.hasAggregatorRole()) {
                return { path: this.homeHref === "" ? "/" : this.homeHref }
            }

            if (this.$permissions.hasBasicUserRole()) {
                return { path: this.homeHref === "" ? "/" : this.homeHref }
            }
        },
        toggleSidebar() {
            this.setSidebarVisibility(!this.isSidebarVisible)
            this.saveSidebarState()
        },
        oemDeviceTranslate(value: string) {
            return translateForOemToLower(this.productName, value)
        },
        scrollToActiveMenuItem() {
            const sidebarWrapper = document.getElementById("sidebar-content-wrapper")
            if (!sidebarWrapper) {
                return
            }

            const allActiveItems = sidebarWrapper.querySelectorAll(
                ".menu-item-active, .sub-menu-item-active",
            )

            let deepestActiveItem: HTMLElement | null = null
            let maxLevel = -1

            allActiveItems.forEach((container) => {
                const titleElement = container.querySelector(".menu-item-title")
                if (titleElement) {
                    const levelMatch = container.className.match(/menu-item-level-(\d+)/)
                    const level = levelMatch ? parseInt(levelMatch[1]) : 0

                    if (level > maxLevel) {
                        maxLevel = level
                        deepestActiveItem = titleElement as HTMLElement
                    }
                }
            })

            if (deepestActiveItem) {
                const itemElement = deepestActiveItem as HTMLElement
                const wrapperRect = sidebarWrapper.getBoundingClientRect()
                const itemRect = itemElement.getBoundingClientRect()

                const isVisible =
                    itemRect.top >= wrapperRect.top && itemRect.bottom <= wrapperRect.bottom

                if (!isVisible) {
                    const itemPositionInList =
                        sidebarWrapper.scrollTop + (itemRect.top - wrapperRect.top)

                    const scrollTarget =
                        itemPositionInList - wrapperRect.height / 2 + itemRect.height / 2

                    sidebarWrapper.scrollTop = scrollTarget
                }
            }
        },
    },
    watch: {
        companies: {
            handler() {
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.scrollToActiveMenuItem()
                    })
                })
            },
            deep: true,
        },
        sidebarMenu: function (newMenu, oldMenu) {
            //@ts-ignore
            if (newMenu.length === 1 && this.$route.fullPath === "/") {
                let location = newMenu[0]
                //@ts-ignore
                this.$router.push(location.href)
            }
        },
        $route: {
            handler: function (to, from) {
                if (this.isOnMobile) {
                    this.setSidebarVisibility(false)
                }

                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.scrollToActiveMenuItem()
                    })
                })
            },
            immediate: true,
        },
    },
    created() {
        this.onResize()

        if (!this.isOnMobile) {
            const savedState = this.loadSidebarState()
            if (savedState !== null) {
                this.setSidebarVisibility(savedState)
            } else {
                this.setSidebarVisibility(true)
            }
        }
    },
    mounted() {
        window.addEventListener("resize", this.handleResize)
        window.addEventListener("resize", this.onResize)

        if (this.isMobileLandscape) {
            this.isFooterOpen = false
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize)
    },
    computed: {
        ...mapGetters("sidebar", {
            sidebarMenu: "sidebarMenu",
            isSidebarVisible: "isSidebarVisible",
            isSidebarLoading: "isSidebarLoading",
            selectedCompany: "company",
            lastLocationId: "locationId",
            location: "location",
            homeHref: "homeHref",
        }),
        ...mapGetters("auth", {
            isImpersonating: "impersonating",
        }),
        ...mapGetters("appLayoutModule", {
            contentRef: "contentRef",
            isLayoutMobile: "isMobile",
            isLayoutMobileLandscape: "isLandscapeMobile",
        }),
        ...mapGetters("siteInfo", {
            productName: "productName",
        }),
        logoUrl() {
            let savedLogo = this.$store.getters["reduxi/getSiteLogo"]

            if (savedLogo) {
                return savedLogo
            }

            //@ts-ignore
            return require(`@/assets/brands/${this.$brand}/images/logo.svg`)
        },
        isMobileLandscape() {
            const isMobileUserAgent =
                /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent,
                )
            const isLandscape = this.windowHeight < this.windowWidth

            return isMobileUserAgent && isLandscape
        },
        siteName() {
            return this.productName
        },
    },
}
</script>

<template>
    <div
        class="sidebar"
        :style="{ maxWidth: sidebarMaxWidth() }"
        :class="{
            closed: !isSidebarVisible && isOnMobile,
            collapsed: !isSidebarVisible && !isOnMobile,
        }"
    >
        <div class="sidebar-header">
            <div v-if="isSidebarVisible" class="text-center sidebar-logo">
                <router-link :to="goToHome()" @click.native="closeSidebar">
                    <img :src="logoUrl" class="logo" alt="logo" />
                </router-link>
                <span class="role-container text-primary text-center py-1">{{
                    $permissions.getActiveRole().name
                }}</span>
                <span
                    v-if="isOnMobile"
                    class="menu-mobile-close expand-icon-wrapper"
                    @click="closeSidebar"
                >
                    <i class="fas fa-times fa-2x text-primary"></i>
                </span>
            </div>

            <button
                v-if="!isLayoutMobile || isLayoutMobileLandscape"
                class="sidebar-toggle-button"
                @click="toggleSidebar"
                :aria-expanded="isSidebarVisible"
            >
                <i :class="isSidebarVisible ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"></i>
            </button>

            <div
                class="border-left border-danger d-flex justify-content-between align-items-center py-2 px-4"
                v-if="isImpersonating && isSidebarVisible"
            >
                <span class="text-danger">{{ $t("Impersonating user!") }}</span>
                <button class="btn btn-sm btn-danger text-white" @click="onExitImpersonationClick">
                    {{ $t("Stop") }}
                </button>
            </div>
        </div>

        <div v-if="isSidebarVisible" class="text-center p-3">
            <i class="global-sidebar-icon fa fa-solid fa-globe text-primary mr-2"></i>
            <span class="user-title">{{ $t("Global Settings") }}</span>
        </div>
        <GlobalSidebarMenu
            class="global-sidebar sidebar-content"
            :menu="getGlobalMenu()"
            v-if="isSidebarVisible"
        />

        <div
            :class="[
                'sidebar-footer',
                { 'footer-closed': !isFooterOpen, 'pt-2': !isMobileLandscape },
            ]"
            v-if="isSidebarVisible"
        >
            <div class="w-100 d-flex my-2" v-if="isMobileLandscape">
                <button
                    @click="toggleFooter"
                    class="toggle-footer-button w-100 border-0"
                    style="background-color: var(--theme-primary); color: var(--theme-tertiary)"
                >
                    <i :class="isFooterOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                </button>
            </div>
            <template v-if="isFooterOpen">
                <router-link
                    :to="{ path: '/admin/search' }"
                    class="btn btn-sidebar btn-sidebar-full p-3"
                    @click.native="onItemClick2"
                >
                    <i class="fa fa-solid fa-magnifying-glass text-primary"></i>
                    <span>
                        {{ $t("admin.search") }}
                    </span>
                </router-link>
                <div class="mb-2"></div>
                <router-link
                    :to="{ path: '/user/settings' }"
                    class="btn btn-sidebar btn-sidebar-full p-3"
                    @click.native="onItemClick2"
                >
                    <i class="fa fa-solid fa-gear text-primary"></i>
                    <span>
                        {{ $t("User Settings") }}
                    </span>
                </router-link>
                <div class="mb-2"></div>
                <router-link
                    :to="{ path: '/' }"
                    class="btn btn-sidebar btn-sidebar-full p-3"
                    @click.native="onItemClick2()"
                >
                    <i class="fa fa-solid fa-globe text-primary"></i>
                    <span class="user-title">{{ $t("Back") }}</span>
                </router-link>

                <div class="mb-2"></div>
            </template>
        </div>
    </div>
</template>

<style lang="scss">
.sidebar {
    transition: max-width 0.3s ease-in-out;
    overflow: hidden;
}

.sidebar.collapsed {
    max-width: 50px;
}

.sidebar.collapsed .sidebar-content,
.sidebar.collapsed .sidebar-header .search-form,
.sidebar.collapsed .sidebar-footer {
    display: none;
}

.sidebar.collapsed .sidebar-toggle-button {
    position: absolute;
    top: 10px;
    left: 0px;
}

.expand-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 50px;
    margin: -15px;
    cursor: pointer;
}

.sidebar-toggle-button {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    background: transparent;
    color: var(--theme-tertiary);
    border: none;
    width: 45px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.sidebar-toggle-button i {
    font-size: 18px;
}

.sidebar-footer {
    .btn {
        box-shadow: 0 3px 6px #00000057;
    }
}
</style>
